import { Currency, CurrencyAmount } from '@kodiak-finance/sdk-core'
import { SupportedChainId } from 'config/chains'
import { UNIVERSAL_BALANCE_HELPER_ADDRESSES } from 'constants/addresses'
import { ZERO_ADDRESS } from 'constants/misc'
import { Address, PublicClient } from 'viem'

interface SwapCall {
  address: string
  calldata: string
  value: string
}

const HELPER_ABI = [
  {
    name: 'getBalance',
    type: 'function',
    stateMutability: 'view',
    inputs: [
      { name: 'token', type: 'address' },
      { name: 'account', type: 'address' },
    ],
    outputs: [{ name: '', type: 'uint256' }],
  },
] as const

export async function simulateSwapWithV1(
  publicClient: PublicClient,
  swapCall: SwapCall,
  account: string,
  recipient: string,
  currencyIn: Currency,
  currencyOut: Currency,
  minAmountOut: CurrencyAmount<Currency>,
  chainId: SupportedChainId
): Promise<void> {
  if (!publicClient || !account || !recipient) {
    throw new Error('Provider, account, or recipient not available')
  }

  const helperAddress = UNIVERSAL_BALANCE_HELPER_ADDRESSES[chainId]
  if (!helperAddress) {
    console.warn(`Universal Balance Helper not deployed on chain ${chainId}. Skipping simulation.`)
    throw new Error(`No balance helper contract for the specified chain ${chainId}`)
  }

  const swapValue = BigInt(swapCall.value || '0x0')

  if (currencyIn.isNative && swapValue === 0n) {
    throw new Error(
      `Native token swap must include value. Input: ${currencyIn.symbol}, swapCall.value: ${swapCall.value}`
    )
  }

  if (!currencyIn.isNative && swapValue !== 0n) {
    const valueInBera = formatAmount(swapValue, 18)
    throw new Error(`ERC20 token swap should not include value. Found: ${valueInBera} BERA (${swapCall.value})`)
  }

  const actualReceived = await getAmountReceivedFromSimulation(
    publicClient,
    swapCall,
    account,
    recipient,
    currencyOut,
    helperAddress
  )

  const minRequired = BigInt(minAmountOut.quotient.toString())

  if (actualReceived < 0n) {
    throw new Error(
      `Swap failed - user would lose tokens: ${formatAmount(actualReceived, currencyOut.decimals)} ${
        currencyOut.symbol
      }. This indicates a routing failure.`
    )
  }

  if (actualReceived < minRequired) {
    const shortfall = minRequired - actualReceived
    const toleranceThreshold = 10n ** BigInt(Math.max(0, currencyOut.decimals - 6))

    if (shortfall < toleranceThreshold) {
      console.warn(
        `Small precision shortfall detected: ${formatAmount(shortfall, currencyOut.decimals)} ${
          currencyOut.symbol
        }, allowing swap to proceed`
      )
      return
    }

    if (actualReceived === 0n) {
      throw new Error(
        `Swap failed - user would receive 0 ${currencyOut.symbol}. Expected at least ${formatAmount(
          minRequired,
          currencyOut.decimals
        )} ${currencyOut.symbol}`
      )
    }

    throw new Error(
      `User will not receive enough tokens: expected at least ${formatAmount(minRequired, currencyOut.decimals)} ${
        currencyOut.symbol
      }, will receive ${formatAmount(actualReceived, currencyOut.decimals)} ${currencyOut.symbol}`
    )
  }
}

async function getAmountReceivedFromSimulation(
  publicClient: PublicClient,
  swapCall: SwapCall,
  account: string,
  recipient: string,
  currencyOut: Currency,
  helperAddress: Address
): Promise<bigint> {
  const tokenAddress = currencyOut.isNative ? ZERO_ADDRESS : currencyOut.wrapped.address

  const getBalanceCalldata = {
    abi: HELPER_ABI,
    functionName: 'getBalance',
    args: [tokenAddress as Address, recipient as Address],
  }
  // swapCall.calldata = swapCall.calldata.slice(0, 10) + 'ab' + swapCall.calldata.slice(12)
  const simulationResults: any = await publicClient.simulateCalls({
    account: account as Address,
    calls: [
      {
        to: helperAddress,
        ...getBalanceCalldata,
      },
      {
        to: swapCall.address as Address,
        data: swapCall.calldata as Address,
        value: BigInt(swapCall.value || '0x0'),
      },
      {
        to: helperAddress,
        ...getBalanceCalldata,
      },
    ],
  })

  if (simulationResults.results[0].status === 'failure') {
    throw new Error(`Balance check (before) failed: ${simulationResults.results[0].error || 'Unknown error'}`)
  }

  if (simulationResults.results[2].status === 'failure') {
    throw new Error(`Balance check (after) failed: ${simulationResults.results[2].error || 'Unknown error'}`)
  }

  if (simulationResults.results[1].status === 'failure') {
    throw new Error(`Swap would fail: ${simulationResults.results[1].error?.message || 'Unknown error'}`)
  } else {
    console.log('simulationResults', simulationResults)
  }

  const balanceBefore = BigInt(simulationResults.results[0].result ?? '0x0')
  const balanceAfter = BigInt(simulationResults.results[2].result ?? '0x0')
  const amountReceived = balanceAfter - balanceBefore

  if (amountReceived < 0n) {
    if (!currencyOut.isNative) {
      return amountReceived
    } else {
      try {
        // Access gasUsed from the swap call result (index 1)
        const swapGasUsed: bigint = simulationResults.results[1].gasUsed ?? 0n
        // Access baseFeePerGas from the block property
        const baseFeePerGas: bigint = simulationResults.block.baseFeePerGas ?? 0n
        const gasCost = swapGasUsed * baseFeePerGas
        const netSwapAmount = amountReceived + gasCost

        if (netSwapAmount > 0n) {
          return netSwapAmount
        } else {
          return 0n
        }
      } catch (error) {
        return 0n
      }
    }
  }

  return amountReceived
}

function formatAmount(amount: bigint, decimals: number): string {
  if (amount === 0n) return '0'

  const isNegative = amount < 0n
  const absAmount = isNegative ? -amount : amount

  const divisor = 10n ** BigInt(decimals)
  const quotient = absAmount / divisor
  const remainder = absAmount % divisor

  let result: string
  if (remainder === 0n) {
    result = quotient.toString()
  } else {
    const remainderStr = remainder.toString().padStart(decimals, '0')
    const trimmed = remainderStr.replace(/0+$/, '')
    result = trimmed === '' ? quotient.toString() : `${quotient}.${trimmed}`
  }

  return isNegative ? `-${result}` : result
}
